/* eslint-disable @next/next/no-img-element */
import {
  CollageElementDataType,
  ProductElementDataType,
} from '@/components/sections/builder/elements/body/collage/type';
import { CollectionElementDataType } from '@/components/sections/builder/elements/body/collection-list/type';
import ProductSetting from '@/components/sections/builder/settings/product-setting';
import { ElementProps } from '@/components/sections/builder/types';
import { Checkbox } from '@/components/ui/checkbox';
import { formatIDR, truncateText } from '@/lib/utils';

import { useBuilderStore } from '@/stores/builder';
import clsx from 'clsx';
import { Tags } from 'lucide-react';
import { useRouter } from 'next/navigation';
import React from 'react';
import { shallow } from 'zustand/shallow';

const defaultData: ProductElementDataType = {
  name: 'Product',
  product: {
    product_name: 'Example product title',
    id: '1',
    slug: 'product-1',
    sell_price: 0,
    product_assets: [
      {
        assets_url: 'https://placehold.co/600x600?text=Placeholder+Product',
        assets_type: 'image',
      },
    ],
  },
};

const Content = ({
  data,
  parentData,
}: {
  parentData: CollageElementDataType;
  data: ProductElementDataType;
}) => {
  const builder = useBuilderStore(
    (state) => ({
      theme: state.theme,
    }),
    shallow
  );
  const firstImage = data.product?.product_assets?.[0]?.assets_url;
  const secondImage = data.product?.product_assets?.[1]?.assets_url;
  const urlProd = data.product?.seo_link?.split('/').reverse()[0];
  const router = useRouter();
  return (
    <>
      <div
        className='image_padding group relative overflow-hidden'
        onClick={() => router.push(`products/${urlProd}`)}
      >
        {data.showSecondImageOnHover ? (
          <>
            <img
              className='img w-full scale-100 duration-500 group-hover:scale-110 group-hover:opacity-0'
              src={firstImage}
              alt={data?.product?.product_name}
            />
            <img
              className='img absolute inset-0 w-full scale-100 opacity-0 duration-500 group-hover:scale-110 group-hover:opacity-100'
              src={secondImage || firstImage}
              alt={data?.product?.product_name}
            />
          </>
        ) : (
          <img className='img w-full' src={firstImage} alt={data?.product?.product_name} />
        )}
      </div>
      <div className={clsx('flex items-center text-left')}>
        <div className='py-2'>
          <div className='text_content card_title mb-2 line-clamp-1 inline-block text-xs'>
            {truncateText(
              data.product?.product_name as string,
              builder.theme.collection?.limitCharacters as number
            )}
          </div>
          <div
            className={clsx(
              'text_content lg:mb-3',
              data.product?.discount_price && 'flex items-center gap-2'
            )}
          >
            {data.product?.discount_price ? (
              <span className='text-xs font-bold italic text-gray-400 line-through'>
                {formatIDR(data.product.sell_price || 0)}
              </span>
            ) : null}
            <span className={clsx(data.product?.discount_price && 'font-bold text-red-600')}>
              {formatIDR(
                data.product?.discount_price
                  ? data.product?.discount_price
                  : data.product?.sell_price || 0
              )}
            </span>
          </div>
        </div>
      </div>
      <style jsx>
        {`
          .text_content {
            color: ${parentData.colorSchemeCollageSection?.text} !important;
          }
        `}
      </style>
    </>
  );
};

export const ElementContent = ({ element }: ElementProps) => {
  const data = (element.data || defaultData) as CollectionElementDataType;
  const parentData = (element.parentData || {}) as CollageElementDataType;

  return (
    <>
      <div
        className={clsx(
          'theme_product_card flex flex-col justify-between overflow-hidden rounded-none '
        )}
      >
        <div className='bg_img'>
          <Content parentData={parentData} data={data} />
        </div>
      </div>
      <style jsx>
        {`
          .theme_product_card {
            background-color: ${parentData.colorSchemeCollageSection?.background};
          }
        `}
      </style>
    </>
  );
};

export const ElementPreviewIcon = Tags;

export function ElementSetting({ element }: ElementProps) {
  const builder = useBuilderStore(
    (state) => ({
      setChildData: state.setChildData,
    }),
    shallow
  );

  const data = (element.data || defaultData) as ProductElementDataType;

  return (
    <div>
      <ProductSetting
        value={data.product}
        onChange={(val) =>
          builder.setChildData(element.parentId as string, element.id, {
            product: val,
          })
        }
      />
      <div className='mt-4 flex gap-x-1'>
        <Checkbox
          id='showSecondImageOnHover'
          aria-label='Select'
          className='mx-2'
          checked={data.showSecondImageOnHover}
          onCheckedChange={(checked) => {
            builder.setChildData(element.parentId as string, element.id, {
              showSecondImageOnHover: !!checked,
            });
          }}
        />
        <label htmlFor='showSecondImageOnHover'>Show second image on hover</label>
      </div>
    </div>
  );
}

const Product = {
  type: 'product',
  group: 'collage',
  defaultData,
  Element: ElementContent,
  Setting: ElementSetting,
  PreviewIcon: ElementPreviewIcon,
  Icon: ElementPreviewIcon,
  childs: [],
};

export default Product;
